import React, {FC, MutableRefObject, useEffect, useRef, useState} from "react";
import {
    Alert,
    Box, Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    InputBase, makeStyles,
    MenuItem,
    Select, TextField,
    Typography
} from "@mui/material";
import {styled} from "@mui/material/styles";
import {GradientButton, GradientButtonWrapper, GradientText} from "./GradientComponents";
import LargePlusIcon from "../icons/LargePlusIcon";
import LargeCaretUpDownIcon from "../icons/LargeCaretUpDownIcon";
import transcriptSamples, {TranscriptSamples} from "../transcriptSamples";
import useModel from "../providers/useModel";

interface AddTranscriptModalBodyProps {
    transcript: string;
    transcriptUpdated: (transcript: string) => void;
}

const AddTranscriptModalBody: FC<AddTranscriptModalBodyProps> = ({transcript, transcriptUpdated}) => {
    return (
        <Box sx={{mt: 2}}>
            {/*<EditableText content={transcript}*/}
            {/*              onChangeText={transcriptUpdated}*/}
            {/*              placeholder={"Paste your transcript here"}*/}
            {/*/>*/}
            <Typography variant={"body2"}>Paste your transcript</Typography>
            <TextField multiline
                       fullWidth
                       placeholder={"Sam: Hey everyone, how are you?\nJohn: I'm good, have been looking forward to this meeting since yesterday.\nSam: That's great, let's get started then."}
                       onChange={(e) => transcriptUpdated(e.target.value)}
                       minRows={20}
                       maxRows={30}>
                {transcript}
            </TextField>
        </Box>
    )
}


const SelectTranscriptInput = styled(InputBase)(({theme}) => ({
    'label + &': {
        marginTop: theme.spacing(3),
    },
    '& .MuiInputBase-input': {
        borderRadius: 999,
        position: 'relative',
        // backgroundColor: theme.palette.background.paper,
        border: '1px solid #E2E8F0',
        fontSize: 14,
        height: 28,
        padding: '4px 8px 4px 8px',
        boxShadow: '0px 1px 2px 0px #0F1A2A14',
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        // '&:focus': {
        //     background: 'linear-gradient(0deg, #E2E8F0, #E2E8F0)',
        // },
        '&:hover': {
            background: 'linear-gradient(0deg, #FEF7F8, #FEF7F8)'
        }
    },
}));

const capitalize = (string: string | undefined) => {
    if (string === undefined) return 'Unknown';
    return string.charAt(0).toUpperCase() + string.slice(1);
}

const HeightAdaptableInputBase = styled(InputBase)({
    flexGrow: 1,
    overflow: 'auto',
    resize: 'none',
    height: '100%',
    minHeight: '50px', // or the minimum height you want
});


interface TranscriptionProps {
    transcript: string;
    transcriptChanged: (transcript: string) => void;
    disabled?: boolean;
}

const Transcription: FC<TranscriptionProps> = ({transcript, transcriptChanged, disabled = false}) => {
    const [selectedTranscriptId, setSelectedTranscriptId] = useState('0');
    const [modalOpen, setModalOpen] = useState(false);
    const [manualTranscript, setManualTranscript] = useState('');
    const [manualTranscriptError, setManualTranscriptError] = useState('');
    const [transcripts, setTranscripts] = useState<TranscriptSamples>(transcriptSamples);
    const {getTokenCount} = useModel();

    const [rows, setRows] = useState({min: 1, max: 1});

    useEffect(() => {
        const resizeTextArea = () => {
            const screenHeight = window.innerHeight - 184;
            const lineHeight = 24; // Change this according to your CSS
            const calculatedRows = Math.floor(screenHeight / lineHeight);
            setRows({ min: calculatedRows, max: calculatedRows });
        };

        resizeTextArea();
        window.addEventListener('resize', resizeTextArea);

        // Cleanup
        return () => window.removeEventListener('resize', resizeTextArea);
    }, []);

    const handleOpen = () => setModalOpen(true);
    const handleClose = () => setModalOpen(false);

    const handleTranscriptChange = (event: { target: { value: string } }) => {
        setSelectedTranscriptId(event.target.value);
        transcriptChanged(transcripts[event.target.value].text);
    }

    useEffect(() => {
        transcriptChanged(transcripts[selectedTranscriptId].text);
    }, [selectedTranscriptId]);

    useEffect(() => {
        if (manualTranscript.length <= 0) {
            setManualTranscriptError('Transcript cannot be empty.');
        }
        // else if (manualTranscript.split(' ').length < 50) {
        //     setManualTranscriptError('Transcript is too short, add transcript with at least 50 words.');
        // }
        else {
            setManualTranscriptError('');
        }
    }, [manualTranscript]);

    return (
        <Box style={{width: '100%'}}>
            <Box style={{paddingLeft: 16}}>
                <Typography sx={{mb: 1}} variant={"subtitle2"} style={{fontWeight: 600}}>Transcript</Typography>
                <Grid container>
                    <Grid item xs={10.5}>
                        <Select
                            style={{
                                width: '100%'
                            }}
                            onChange={handleTranscriptChange}
                            value={selectedTranscriptId}
                            input={<SelectTranscriptInput/>}
                            disabled={disabled}
                            renderValue={(selected) => (
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                    {selectedTranscriptId && (
                                        <Chip key={selected} label={
                                            <GradientText disabled={disabled}>
                                                <Typography component="span" variant={"body2"}
                                                            style={{fontWeight: 500}}>{capitalize(transcripts[selected].type)}: </Typography>
                                                <Typography component="span"
                                                            variant={"body2"}>{transcripts[selected].title}</Typography>
                                            </GradientText>
                                        }
                                              style={{
                                                  height: 28,
                                                  padding: '4px 4px 4px 4px',
                                                  background: disabled ? 'linear-gradient(270deg, rgba(166, 166, 166, 0.16) 0%, rgba(135, 135, 135, 0.16) 38.54%, rgba(156, 156, 156, 0.16) 100%)'
                                                      : 'linear-gradient(270deg, rgba(226, 138, 156, 0.16) 0%, rgba(211, 120, 242, 0.16) 38.54%, rgba(90, 156, 255, 0.16) 100%)',
                                              }}/>
                                    )}
                                </Box>
                            )}
                            IconComponent={LargeCaretUpDownIcon}
                        >
                            {
                                Object.keys(transcripts).map((key) => (
                                    <MenuItem key={key} value={key}>{transcripts[key].title}</MenuItem>
                                ))
                            }

                        </Select>
                    </Grid>
                    <Grid item xs={1.5} style={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                    }}>
                        <GradientButton onClick={handleOpen}
                                        disabled={disabled}
                                        style={{
                                            borderRadius: 999,
                                            minWidth: 40,
                                            width: 40
                                        }}>
                            <LargePlusIcon/>
                        </GradientButton>
                    </Grid>
                </Grid>
            </Box>
            <Divider sx={{my: 2}}/>
            <Box sx={{mt: 2}}
                 style={{
                     paddingLeft: 16, paddingRight: 16,
                     display: 'flex',
                     flexDirection: 'column'
                 }}>
                {/*<MuiMarkdown>*/}
                {/*    {transcript.replaceAll('\n\n', '\n').replaceAll('\n', '<br><br>')}*/}
                {/*</MuiMarkdown>*/}
                <InputBase multiline
                           fullWidth
                           minRows={10}
                           maxRows={rows.max}
                           value={transcript}
                           readOnly={true}
                           style={{
                               flexGrow: 1,
                               overflow: 'auto'
                           }}
                />
            </Box>
            <Dialog open={modalOpen}
                    maxWidth={'lg'}
                    fullWidth
                    onClose={handleClose}>
                <DialogTitle>
                    <Typography variant={"h6"}>Add Transcript</Typography>
                </DialogTitle>
                <DialogContent>
                    {
                        manualTranscriptError && (
                            <Alert severity="error">{manualTranscriptError}</Alert>)
                    }
                    <AddTranscriptModalBody transcript={manualTranscript} transcriptUpdated={async (transcript) => {
                        // const tokenCount = await getTokenCount(transcript);
                        // if (tokenCount > 7168) {
                        //     const wordCount = transcript.split(' ').length;
                        //     const ratio = tokenCount / wordCount;
                        //     const wordCountToKeep = 7168 / ratio;
                        //     transcript = transcript.split(' ').slice(0, wordCountToKeep).join(' ');
                        // }
                        setManualTranscript(transcript);
                    }}/>
                </DialogContent>
                <DialogActions>
                    <GradientButton gradientBackground={true}
                                    disabled={manualTranscriptError.length > 0 || !manualTranscript}
                                    onClick={() => {
                                        transcriptChanged(manualTranscript);
                                        const id = `${Object.keys(transcripts).length}`;
                                        setTranscripts({
                                            ...transcripts, [id]: {
                                                title: manualTranscript.slice(0, 20) + '...',
                                                type: 'manual',
                                                text: manualTranscript
                                            }
                                        });
                                        setSelectedTranscriptId(id);
                                        handleClose();
                                    }}>
                        <Typography variant={"subtitle2"}>Done</Typography>
                    </GradientButton>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

export default Transcription;
